"use client";

import { Core } from "@faire/design-tokens";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { NonBreaking } from "@faire/web/common/string";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import capitalize from "lodash/capitalize";
import React, { Suspense, use } from "react";

import { DiscoverPageHeroBannerData } from "@faire/retailer/app/(app)/visitor/[locale]/(pagesWithModal)/(defaultHeader)/discover/fetchHeroBannerData";
import { SignUpElement, SignUpType } from "@faire/retailer/lib/popSignUp";
import {
  DiscoverPageImageryBannerPhase3,
  VerticalizedBannerLoadingSkeleton,
} from "@faire/retailer/views/search-v2/LoggedOutDiscoverHeaderBanner/LoggedOutDiscoverPageHeroBannerPhase3";

import * as styles from "./LoggedOutDiscoverPageHeroBanner.css";
import { SignUpButton } from "./SignUpButton";

const DiscoverPageHeroText: React.FC<{ searchTerm: string }> = ({
  searchTerm,
}) => {
  const productSearchString = capitalize(searchTerm);

  return (
    <div className={styles.contentWrapper}>
      <Typography
        variant={{
          mobileAndAbove: "pageHeaderSerifRegular",
          tabletAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
          xLargeAndAbove: "displayMSerifRegular",
        }}
        as="h1"
        color={Core.text.inverse}
      >
        <StrictLocalMsg
          defaultMessage="Wholesale {queryProductName} for your{nbsp}store"
          description={{
            text: "header for discover page explaining you can buy the item at wholesale prices",
            img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140b134967137191.png",
          }}
          values={{
            queryProductName: productSearchString,
            nbsp: NonBreaking.space,
          }}
        />
      </Typography>
      <Typography
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          desktopAndAbove: "subheadingSansRegular",
        }}
        color={Core.text.inverse}
      >
        <StrictLocalMsg
          defaultMessage="From frames to decorative objects and everything in between, whatever you're looking for, find it on{nbsp}Faire."
          description={{
            text: "subheading for discover page, encouraging retailers to browse the site",
            img: "https://cdn.faire.com/fastly/7ab0bf0cdb8db14e66933ffe35b46db73faa9f353d04a80d140b134967137191.png",
          }}
          values={{
            nbsp: NonBreaking.space,
          }}
        />
      </Typography>
    </div>
  );
};

const DiscoverPageImageryBanner: React.FC<{ searchTerm: string }> = ({
  searchTerm,
}) => {
  const image = IImage.build({
    url: "https://cdn.faire.com/fastly/47f5406bc2522e2b276072f64a9b376bb7013332c5cbfb185a5771a3023b691e.png",
  });

  const pictureSizes = [
    `(min-width: ${MIN_WIDTH_BREAKPOINTS.XXLARGE}px) 1200px`,
    `(min-width: ${MIN_WIDTH_BREAKPOINTS.DESKTOP}px) 800px`,
    `(min-width: ${MIN_WIDTH_BREAKPOINTS.TABLET}px) 700px`,
    `454px`,
  ].join(", ");

  const fallbackSrc = optimizeImageUrl(image, true, {
    width: 1200,
  });
  const srcSet = [2400, 1600, 1200, 800, 700, 454]
    .map((width) => {
      const url = optimizeImageUrl(image, true, { width });
      return `${url} ${width}w`;
    })
    .join(", ");

  return (
    <div className={styles.rowColumnReverse}>
      <div className={styles.bannerTextWrapper}>
        <DiscoverPageHeroText searchTerm={searchTerm} />

        <SignUpButton
          type={SignUpType.GENERIC}
          element={SignUpElement.TOP_BANNER}
          sourceId="logged_out_discover_hero_banner"
          variant="secondary"
          dataTestId="banner-cta-button"
        >
          <StrictLocalMsg
            defaultMessage="Sign up for free"
            description={{
              text: "sign up cta for discover page header",
              img: "https://cdn.faire.com/fastly/d21596fe32412c4ed5e66b8126825c2cec443186715ab55c7eda5f5488718062.png",
            }}
          />
        </SignUpButton>
      </div>
      <picture className={styles.bannerImageWrapper}>
        <source srcSet={srcSet} sizes={pictureSizes} />
        <img
          className={styles.bannerImage}
          src={fallbackSrc}
          data-test-id="banner-image"
        />
      </picture>
    </div>
  );
};

const LoggedOutDiscoverPageHeroBannerBase: React.FC<
  DiscoverPageHeroBannerData
> = (props) => {
  const showVerticalizedBanner = use(
    props.isSettingVerticalizedBannerEnabledPromise
  );
  return showVerticalizedBanner ? (
    <DiscoverPageImageryBannerPhase3 {...props} />
  ) : (
    <DiscoverPageImageryBanner searchTerm={props.searchTerm} />
  );
};

export const LoggedOutDiscoverPageHeroBanner: React.FC<
  DiscoverPageHeroBannerData
> = (props) => {
  return (
    <Suspense fallback={<VerticalizedBannerLoadingSkeleton />}>
      <LoggedOutDiscoverPageHeroBannerBase {...props} />
    </Suspense>
  );
};
